import type { ApolloError } from "@apollo/client";

import { query as Q } from "@/app-utils/graphql/query.ts";
import type { SampleType } from "@/graphql.ts";
import {
  GetSampleTypesDocument,
  type GetSampleTypesQuery,
  type GetSampleTypesQueryVariables,
  GetUserGroupIdOfProjectDocument,
  type GetUserGroupIdOfProjectQuery,
  type GetUserGroupIdOfProjectQueryVariables,
} from "@/modules/workflow/graphql/workflow.gql.ts";
import type { WorkflowParameter } from "@/modules/workflow/utils/types.ts";
import { PUBLIC_PROJECT_GROUP_ID } from "@/settings/settings.ts";

/**
 *
 * @param parameter A workflow parameter
 * @returns Whether a given parameter is of type Tracker or not.
 */
export function isTrackerParam(parameter: WorkflowParameter) {
  return parameter.typeName === "TrackerParam";
}

/**
 *
 * @param parameter A workflow parameter
 * @returns Whether a given parameter is of type Sample or not.
 */
export function isSampleParam(parameter: WorkflowParameter) {
  return parameter.typeName === "SampleParam";
}

/**
 * Checks if any activity used in the workflow can use a sample type.
 * This function drives the disabled state of the global sample type selector.
 *
 * @param parameters List of workflow parameters
 * @returns Boolean: True if any activity can use a sample type, false otherwise
 */
export function canAnyActivityUseSampleType(parameters: WorkflowParameter[]) {
  const parametersWithTracker = getParametersAcceptingSampleType(parameters);
  return parametersWithTracker?.length > 0;
}

/**
 *
 * Returns a list of nodes that accept a sample type. A node becomes part
 * of this list if it has a tracker or a sample parameter.
 *
 * @param parameters List of workflow parameters
 * @returns List of nodes that accept a sample type
 */
export function getParametersAcceptingSampleType(parameters: WorkflowParameter[]) {
  return parameters.filter((parameter) => isTrackerParam(parameter) || isSampleParam(parameter));
}

/**
 *
 * Checks if any sample parameter requires a sample type selection.
 * This is the case when there is at least one mandatory sample parameter
 * and no sample type is set yet.
 *
 * @param parameters List of workflow parameters
 * @param sampleTypeId
 * @returns True if sample type must be set, false otherwise
 */
export function isSampleTypeSelectionRequired(parameters: WorkflowParameter[], sampleTypeId: number | undefined) {
  const parametersWithTracker = getParametersAcceptingSampleType(parameters);
  return !sampleTypeId && parametersWithTracker?.some((parameter) => parameter.schema?.isOptional() !== true);
}

/**
 * Checks if any of the sample parameters is staged.
 *
 * @param parameters List of workflow parameters
 * @returns True if any parameter is a staged sample selection, false otherwise
 */
export function hasStagedSampleSelectionParams(parameters: WorkflowParameter[]) {
  return parameters.some((parameter) => parameter.staged && isSampleParam(parameter));
}

const qetUserGroupIdOfProjectQuery = Q<GetUserGroupIdOfProjectQuery, GetUserGroupIdOfProjectQueryVariables>(
  GetUserGroupIdOfProjectDocument,
);
const getSampleTypesQuery = Q<GetSampleTypesQuery, GetSampleTypesQueryVariables>(GetSampleTypesDocument);

export async function getSampleTypes(projectId: number) {
  const userGroupId = await qetUserGroupIdOfProjectQuery({ projectId }).then((response) => {
    if (response.error) {
      throw new Error((response.error as Error | ApolloError).message);
    }

    return response.data?.project?.userGroup?.id;
  });

  if (userGroupId == null) {
    throw new Error("Could not find user group for project");
  }

  return await getSampleTypesQuery({
    userGroupIds:
      userGroupId === PUBLIC_PROJECT_GROUP_ID ? [PUBLIC_PROJECT_GROUP_ID] : [userGroupId, PUBLIC_PROJECT_GROUP_ID],
  })
    .then((response) => {
      if (response.error) {
        throw new Error((response.error as Error | ApolloError).message);
      }

      return response.data?.sample?.sampleTypes ?? [];
    })
    .then((sampleTypes) => sampleTypes.filter((sampleType): sampleType is SampleType => sampleType != undefined));
}
